import { useWorkspaceStore } from '@/store/workspaceStore';
import { Modal } from '@/components/Modal';
import { SectionCard } from '@/components/SectionCard';
import { Select } from '@/components/Select';
import { Slider } from '@/components/Slider';
import { Toggle } from '@/components/Toggle';
import type { FrameRate, QualityPreset } from '@/types';

export function SharedDefaultsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const shared = useWorkspaceStore((s) => s.workspace.shared);
  const updateShared = useWorkspaceStore((s) => s.updateShared);

  return (
    <Modal open={open} title="Workspace defaults" onClose={onClose}>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
        These apply to every Reel that hasn't overridden them individually. Great for keeping bulk batches consistent.
      </p>
      <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
        <SectionCard title="Voice" defaultOpen={false}>
          <Select
            label="Default provider"
            value={shared.voiceover.provider}
            options={[
              { value: 'google', label: 'Google Cloud Text-to-Speech' },
              { value: 'openai', label: 'OpenAI TTS-1' },
            ]}
            onChange={(v) => updateShared('voiceover', { provider: v as typeof shared.voiceover.provider })}
          />
          <div className="grid grid-cols-2 gap-2">
            <Slider label="Speed" value={shared.voiceover.speakingRate} min={0.25} max={4} step={0.05} formatValue={(v) => `${v.toFixed(2)}×`} onChange={(v) => updateShared('voiceover', { speakingRate: v })} />
            {shared.voiceover.provider === 'google' && (
              <Slider label="Pitch" value={shared.voiceover.pitch} min={-10} max={10} step={0.5} onChange={(v) => updateShared('voiceover', { pitch: v })} />
            )}
          </div>
          <Slider label="Volume" value={shared.voiceover.volume} min={0} max={1.5} step={0.05} formatValue={(v) => `${Math.round(v * 100)}%`} onChange={(v) => updateShared('voiceover', { volume: v })} />
        </SectionCard>

        <SectionCard title="Captions" defaultOpen={false}>
          <Toggle label="Enabled by default" checked={shared.captions.enabled} onChange={(v) => updateShared('captions', { enabled: v })} />
          <Select
            label="Casing"
            value={shared.captions.casing}
            options={[
              { value: 'original', label: 'Original' },
              { value: 'uppercase', label: 'Uppercase' },
              { value: 'lowercase', label: 'Lowercase' },
            ]}
            onChange={(v) => updateShared('captions', { casing: v as typeof shared.captions.casing })}
          />
        </SectionCard>

        <SectionCard title="Music" defaultOpen={false}>
          <Slider label="Default volume" value={shared.music.volume} min={0} max={1} step={0.05} formatValue={(v) => `${Math.round(v * 100)}%`} onChange={(v) => updateShared('music', { volume: v })} />
          <Toggle label="Auto-duck under voiceover" checked={shared.music.duckingEnabled} onChange={(v) => updateShared('music', { duckingEnabled: v })} />
        </SectionCard>

        <SectionCard title="Watermark" defaultOpen={false}>
          <Toggle label="Enabled by default" checked={shared.watermark.enabled} onChange={(v) => updateShared('watermark', { enabled: v })} />
          <Slider label="Size" value={shared.watermark.widthPercent} min={5} max={50} onChange={(v) => updateShared('watermark', { widthPercent: v })} formatValue={(v) => `${v}%`} />
          <Slider label="Opacity" value={shared.watermark.opacity} min={0} max={1} step={0.05} onChange={(v) => updateShared('watermark', { opacity: v })} formatValue={(v) => `${Math.round(v * 100)}%`} />
        </SectionCard>

        <SectionCard title="Export" defaultOpen={false}>
          <div className="grid grid-cols-2 gap-2">
            <Select
              label="Frame rate"
              value={String(shared.export.frameRate)}
              options={[24, 25, 30, 60].map((f) => ({ value: String(f), label: `${f} fps` }))}
              onChange={(v) => updateShared('export', { frameRate: Number(v) as FrameRate })}
            />
            <Select
              label="Quality"
              value={shared.export.quality}
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'standard', label: 'Standard' },
                { value: 'high', label: 'High' },
              ]}
              onChange={(v) => updateShared('export', { quality: v as QualityPreset })}
            />
          </div>
        </SectionCard>
      </div>
    </Modal>
  );
}
